import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Building2, Globe, MapPin, Users, User, Phone, Mail, FileSignature, Upload, PenTool, Trash2, CheckCircle2 } from "lucide-react";
import { validateContactNumber } from "../../../utils/formValidators";

export default function Step1GroupInfo({ formData, setFormData, errors }) {
  const canvasRef = useRef(null);
  const [phoneFieldValid, setPhoneFieldValid] = useState(null);
  const [signatureMode, setSignatureMode] = useState(formData.primaryContactSignature ? (typeof formData.primaryContactSignature === "string" ? "draw" : "upload") : null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePrimaryPhoneChange = (value) => {
    handleChange("primaryContactPhone", value);
    const validation = validateContactNumber(value);
    setPhoneFieldValid(validation.isValid ? "valid" : null);
  };

  const numAttendees = parseInt(formData.numberOfAttendees, 10) || 0;

  const signatureBase64 = typeof formData.primaryContactSignature === "string" ? formData.primaryContactSignature : null;

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) {
      setFormData((prev) => ({ ...prev, primaryContactSignature: null }));
      return;
    }
    if (!file.type.startsWith("image/")) {
      setFormData((prev) => ({ ...prev, primaryContactSignature: null }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFormData((prev) => ({ ...prev, primaryContactSignature: reader.result }));
    };
    reader.readAsDataURL(file);
    setSignatureMode("upload");
  };

  const getCanvasPoint = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const ctx = canvasRef.current?.getContext("2d");
    const p = getCanvasPoint(e);
    if (ctx && p) {
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
    }
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const ctx = canvasRef.current?.getContext("2d");
    const p = getCanvasPoint(e);
    if (ctx && p) {
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      setHasDrawn(true);
    }
  };

  const stopDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || signatureMode !== "draw") return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(rect.width * dpr);
    canvas.height = Math.floor(rect.height * dpr);
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    ctx.strokeStyle = "#0d9488";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    return () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    };
  }, [signatureMode]);

  const saveSignatureFromCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasDrawn) return;
    const dataUrl = canvas.toDataURL("image/png");
    setFormData((prev) => ({ ...prev, primaryContactSignature: dataUrl }));
  };

  const clearSignature = () => {
    setFormData((prev) => ({ ...prev, primaryContactSignature: null }));
    setSignatureMode(null);
    setHasDrawn(false);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };
  const maxAttendees = 50;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm sm:p-8"
    >
      <div className="flex gap-3 items-center mb-6">
        <div className="flex justify-center items-center w-10 h-10 text-white bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg shadow-md">
          <Users size={20} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Group Information</h2>
      </div>
      <p className="mb-6 text-sm text-gray-600">Enter your organization details. Primary contact is required.</p>

      {/* Required fields */}
      <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
            <Building2 size={16} className="text-teal-600" />
            Organization Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.organizationName || ""}
            onChange={(e) => handleChange("organizationName", e.target.value)}
            className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-teal-500 ${
              errors?.organizationName ? "border-red-300 bg-red-50" : "border-gray-300"
            }`}
            placeholder="e.g. Acme Ltd, Diocese, Arch Diocese, Group"
          />
          {errors?.organizationName && <p className="mt-1 text-sm text-red-500">{errors.organizationName}</p>}
        </div>

        <div>
          <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
            <Globe size={16} className="text-teal-600" />
            Country <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.groupCountry || ""}
            onChange={(e) => handleChange("groupCountry", e.target.value)}
            className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-teal-500 ${
              errors?.groupCountry ? "border-red-300 bg-red-50" : "border-gray-300"
            }`}
            placeholder="e.g. Ghana"
          />
          {errors?.groupCountry && <p className="mt-1 text-sm text-red-500">{errors.groupCountry}</p>}
        </div>

        <div>
          <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
            <MapPin size={16} className="text-teal-600" />
            City <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.groupCity || ""}
            onChange={(e) => handleChange("groupCity", e.target.value)}
            className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-teal-500 ${
              errors?.groupCity ? "border-red-300 bg-red-50" : "border-gray-300"
            }`}
            placeholder="e.g. Accra"
          />
          {errors?.groupCity && <p className="mt-1 text-sm text-red-500">{errors.groupCity}</p>}
        </div>

        <div>
          <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
            <Users size={16} className="text-teal-600" />
            Number of Attendees <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min={2}
            max={maxAttendees}
            value={formData.numberOfAttendees ?? ""}
            onChange={(e) => handleChange("numberOfAttendees", e.target.value)}
            className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-teal-500 ${
              errors?.numberOfAttendees ? "border-red-300 bg-red-50" : "border-gray-300"
            }`}
            placeholder="2"
          />
          {errors?.numberOfAttendees && <p className="mt-1 text-sm text-red-500">{errors.numberOfAttendees}</p>}
          <p className="mt-1 text-xs text-gray-500">Min 2, max {maxAttendees}</p>
        </div>
      </div>

      {/* Required: Primary Contact Person */}
      <div className="pt-6 border-t border-gray-200">
        <h3 className="mb-4 text-lg font-semibold text-gray-800">Primary Contact Person</h3>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
              <User size={16} className="text-gray-500" />
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.primaryContactName || ""}
              onChange={(e) => handleChange("primaryContactName", e.target.value)}
              className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-teal-500 ${
                errors?.primaryContactName ? "border-red-300 bg-red-50" : "border-gray-300"
              }`}
              placeholder="Contact person full name"
            />
            {errors?.primaryContactName && <p className="mt-1 text-sm text-red-500">{errors.primaryContactName}</p>}
          </div>
          <div>
            <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
              <Phone size={16} className="text-gray-500" />
              Phone number (include country code) <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="tel"
                value={formData.primaryContactPhone || ""}
                onChange={(e) => handlePrimaryPhoneChange(e.target.value)}
                className={`w-full px-4 py-3 pr-10 rounded-lg border outline-none focus:ring-2 focus:ring-teal-500 ${
                  errors?.primaryContactPhone
                    ? "border-red-300 bg-red-50"
                    : phoneFieldValid === "valid"
                      ? "border-green-300 bg-green-50"
                      : "border-gray-300"
                }`}
                placeholder="e.g. 233241234567 or 14155552671"
              />
              {phoneFieldValid === "valid" && (
                <CheckCircle2 className="absolute top-3.5 right-3 text-green-500" size={20} />
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Enter digits only (10 to 15) and do not include a leading +.
            </p>
            {errors?.primaryContactPhone && <p className="mt-1 text-sm text-red-500">{errors.primaryContactPhone}</p>}
          </div>
          <div>
            <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
              <Mail size={16} className="text-gray-500" />
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.primaryContactEmail || ""}
              onChange={(e) => handleChange("primaryContactEmail", e.target.value)}
              className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-teal-500 ${
                errors?.primaryContactEmail ? "border-red-300 bg-red-50" : "border-gray-300"
              }`}
              placeholder="contact@example.com"
            />
            {errors?.primaryContactEmail && <p className="mt-1 text-sm text-red-500">{errors.primaryContactEmail}</p>}
          </div>
          <div className="md:col-span-2">
            <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
              <FileSignature size={16} className="text-gray-500" />
              Signature (upload or sign here) <span className="text-red-500">*</span>
            </label>
            <p className="mb-3 text-xs text-gray-500">Required. Upload an image or draw your signature below.</p>

            {signatureBase64 ? (
              <div className="space-y-3">
                <p className="text-xs font-semibold text-teal-700">Preview</p>
                <div className="flex flex-wrap gap-4 items-start">
                  <div className="overflow-hidden rounded-lg border-2 border-teal-200 bg-white p-2">
                    <img
                      src={signatureBase64}
                      alt="Signature preview"
                      className="max-h-24 w-auto object-contain"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={clearSignature}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg border border-red-200 hover:bg-red-100"
                  >
                    <Trash2 size={16} />
                    Clear signature
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setSignatureMode("upload")}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border-2 transition-colors ${
                      signatureMode === "upload" ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-200 bg-white text-gray-600 hover:border-teal-300"
                    }`}
                  >
                    <Upload size={18} />
                    Upload image
                  </button>
                  <button
                    type="button"
                    onClick={() => setSignatureMode("draw")}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border-2 transition-colors ${
                      signatureMode === "draw" ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-200 bg-white text-gray-600 hover:border-teal-300"
                    }`}
                  >
                    <PenTool size={18} />
                    Sign here
                  </button>
                </div>

                {signatureMode === "upload" && (
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-teal-50 file:text-teal-700"
                  />
                )}

                {signatureMode === "draw" && (
                  <div className="space-y-3">
                    <div className="rounded-lg border-2 border-teal-200 bg-gray-50 overflow-hidden touch-none">
                      <canvas
                        ref={canvasRef}
                        className="block w-full h-40 cursor-crosshair"
                        style={{ touchAction: "none" }}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={saveSignatureFromCanvas}
                      disabled={!hasDrawn}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Save signature
                    </button>
                  </div>
                )}
              </>
            )}
            {errors?.primaryContactSignature && <p className="mt-1 text-sm text-red-500">{errors.primaryContactSignature}</p>}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
